import type { Bundle } from "../schema/store.js";
import type { AchievementDef, GrowthMissionDef, IntelMissionDef, MissionDef, PassTierDef, ScoreSource } from "../schema/events.js";
import { BUILDINGS } from "./buildings.js";
import { BATTLE_PASS_TIER_CREDITS, STORE_TABS } from "./store.js";

const sp = (type: string, minutes: number, qty: number) => ({ itemId: `itm.speedup_${type}_${minutes}`, qty });
const crate = (res: string, size: string, qty: number) => ({ itemId: `itm.crate_${res}_${size}`, qty });
const N = (de: string, en: string) => ({ de, en });

/** Twelve daily missions (70-events.md R60); each pays activity points toward the daily chests. */
export const DAILY_MISSIONS: readonly MissionDef[] = [
  { id: "dm.upgrade", name: N("Ein Gebäude ausbauen", "Upgrade a building"), source: "build.level", target: 1, points: 10 },
  { id: "dm.research", name: N("Eine Forschung abschließen", "Finish a research"), source: "research.level", target: 1, points: 10 },
  { id: "dm.gather", name: N("20 000 Ressourcen sammeln", "Gather 20 000 resources"), source: "gather", target: 20, per: 1000, points: 10 },
  { id: "dm.kill", name: N("Drei Rudel vernichten", "Kill three packs"), source: "pack.kill", target: 3, points: 10 },
  { id: "dm.pull", name: N("Einmal fabrizieren", "Fabricate once"), source: "workshop.pull", target: 1, points: 5 },
  { id: "dm.arena", name: N("Zwei Arenakämpfe", "Two arena fights"), source: "arena.fight", target: 2, points: 10 },
  { id: "dm.assist", name: N("Einem Nachbarn helfen", "Assist a neighbour"), source: "assist", target: 1, points: 5 },
  { id: "dm.stamina", name: N("30 Ausdauer ausgeben", "Spend 30 stamina"), source: "stamina.spent", target: 30, points: 10 },
  { id: "dm.chat", name: N("Im Chat schreiben", "Say something in chat"), source: "chat", target: 1, points: 5 },
  { id: "dm.expedition", name: N("Eine Expeditionsstufe", "Clear an expedition stage"), source: "expedition.clear", target: 1, points: 10 },
  { id: "dm.repair", name: N("Eine Einheit reparieren", "Repair a unit"), source: "repair", target: 1, points: 5 },
  { id: "dm.buy", name: N("Ein Angebot kaufen", "Buy any offer"), source: "store.buy", target: 1, points: 10 },
];

export const ACTIVITY_CHESTS: readonly { points: number; reward: Bundle }[] = [
  { points: 20, reward: { items: [sp("general", 15, 2), crate("rat", "10k", 1)] } },
  { points: 40, reward: { items: [{ itemId: "itm.chest_bronze", qty: 1 }, { itemId: "itm.cdc", qty: 10 }] } },
  { points: 60, reward: { items: [sp("general", 60, 2), crate("aly", "1k", 3)] } },
  { points: 80, reward: { items: [{ itemId: "itm.chest_silver", qty: 1 }, { itemId: "itm.stamina_20", qty: 1 }] } },
  { points: 100, reward: { items: [{ itemId: "itm.fab", qty: 1 }, { itemId: "itm.cdc", qty: 30 }], credits: 20 } },
];

/** Intel Hub missions, batched every six hours; storage 12 + Intel Hub level (R60). */
export const INTEL_MISSIONS: readonly IntelMissionDef[] = [
  { id: "intel.rescue", name: N("Überlebende retten", "Survivor rescue"), kind: "rescue", minutes: 20, reward: { items: [crate("rat", "10k", 2)], credits: 2 }, weight: 30 },
  { id: "intel.elite", name: N("Elite erlegen", "Elite kill"), kind: "elite", minutes: 45, reward: { items: [{ itemId: "itm.cdc", qty: 15 }] }, weight: 20 },
  { id: "intel.pickup", name: N("Ressourcen bergen", "Resource pickup"), kind: "pickup", minutes: 15, reward: { items: [crate("pol", "10k", 2)] }, weight: 25 },
  { id: "intel.dig", name: N("Schatzsuche", "Treasure dig"), kind: "dig", minutes: 60, reward: { items: [{ itemId: "itm.chest_bronze", qty: 1 }], credits: 5 }, weight: 15 },
  { id: "intel.drone", name: N("Drohnenteile", "Drone parts"), kind: "drone", minutes: 30, reward: { items: [{ itemId: "itm.drone_core", qty: 1 }, { itemId: "itm.nano", qty: 5 }] }, weight: 10 },
];
export const INTEL_BATCH_HOURS = 6;
export const INTEL_BATCH_SIZE = 3;
export const INTEL_STORAGE_BASE = 12;

/**
 * The Radar (70-events.md R89, D105): every intel mission is a tile within eight tiles of the base. Survivors
 * and crates are collected with a tap, a dig takes a march and twenty minutes on site, a drone wreck a march,
 * an elite a fight. A mission tile lasts eight hours; a full storage stops the batch timer; a scan rolls the
 * next batch early — one free a day, then credits.
 */
export const RADAR = { radiusTiles: 8, expireHours: 8, digMinutes: 20, scan: { freePerDay: 1, credits: 20 } } as const;

/**
 * The helicopter (D105): Saturday at noon a wreck goes down within four tiles of every Dominion. Up to eight
 * members salvage it, one march of fifteen minutes each, for a share; the fourth salvage pays the alliance a
 * gift in coins; the wreck is gone after six hours or the eighth salvage.
 */
export const RADAR_HELI = {
  weekday: 6,
  hour: 12,
  radiusTiles: 4,
  salvageMinutes: 15,
  maxSalvagers: 8,
  lifetimeHours: 6,
  share: { items: [crate("aly", "1k", 4), { itemId: "itm.cdc", qty: 20 }, sp("general", 60, 1)], credits: 15 } as Bundle,
  giftAtSalvage: 4,
  giftCoins: 100,
} as const;

/** Seven-day login cycle; day 28 of the month adds the S-tier UFR chest (R60). */
export const LOGIN_CALENDAR: readonly Bundle[] = [
  { items: [crate("rat", "10k", 2)], credits: 5 },
  { items: [sp("general", 15, 3)], credits: 5 },
  { items: [{ itemId: "itm.cdc", qty: 20 }], credits: 10 },
  { items: [crate("aly", "1k", 5)], credits: 10 },
  { items: [{ itemId: "itm.fab", qty: 1 }], credits: 15 },
  { items: [sp("general", 60, 2), { itemId: "itm.stamina_20", qty: 1 }], credits: 15 },
  { items: [{ itemId: "itm.chest_silver", qty: 1 }, { itemId: "itm.cdc", qty: 40 }], credits: 30 },
];
/** Day of the month that opens the S-tier UFR chest (R60). */
export const MONTHLY_DAY = 28;
export const MONTHLY_DAY28_CHEST: Bundle = { items: [{ itemId: "itm.ufr_s", qty: 20 }, { itemId: "itm.ffab", qty: 2 }], credits: 100 };

/** Growth chain: 20 chapters × 10 missions covering every system, chapter chests, units at 5/10/15/20 (R64). */
const CHAPTER_THEMES: { title: [string, string]; focus: string }[] = [
  { title: ["Erste Schritte", "First Steps"], focus: "base" },
  { title: ["Versorgung", "Supply"], focus: "gather" },
  { title: ["Fabrikation", "Fabrication"], focus: "units" },
  { title: ["Die Todeszone", "The Dead Zone"], focus: "world" },
  { title: ["Nachbarn", "Neighbours"], focus: "social" },
  { title: ["Forschung", "Research"], focus: "research" },
  { title: ["Der Store", "The Store"], focus: "store" },
  { title: ["Arena", "Arena"], focus: "arena" },
  { title: ["Expeditionen", "Expeditions"], focus: "expedition" },
  { title: ["Plünderer", "Raiders"], focus: "pvp" },
  { title: ["Werkstatt", "Armory"], focus: "modules" },
  { title: ["Hives", "Hives"], focus: "hive" },
  { title: ["Verteidigung", "Defence"], focus: "defence" },
  { title: ["Sammler", "Collector"], focus: "collection" },
  { title: ["Belagerung", "Siege"], focus: "siege" },
  { title: ["VIP", "VIP"], focus: "vip" },
  { title: ["Titan", "Titan"], focus: "boss" },
  { title: ["Nekropole", "Necropolis"], focus: "ruins" },
  { title: ["Kriegsherr", "Warlord"], focus: "warlord" },
  { title: ["Ground Zero", "Ground Zero"], focus: "endgame" },
];

const BUILDING_IDS = BUILDINGS.map((b) => b.id);
// Derived, never listed again: a hand-kept copy silently stopped covering the store the day the
// HQ milestone tab was added, and the growth chain is meant to walk a player through all of it.
const STORE_TAB_LINKS: readonly string[] = STORE_TABS;

function growthMission(chapter: number, index: number, theme: (typeof CHAPTER_THEMES)[number]): GrowthMissionDef {
  const level = chapter;
  // Missions 1 and 10 of every chapter are building missions (40 in total, every building at least once);
  // mission 7 of every chapter is a store mission (20 in total, every store tab at least once).
  const buildingId = BUILDING_IDS[((chapter - 1) * 2 + (index === 9 ? 1 : 0)) % BUILDING_IDS.length]!;
  const tab = STORE_TAB_LINKS[(chapter - 1) % STORE_TAB_LINKS.length]!;
  const sources: ScoreSource[] = ["build.level", "gather", "workshop.pull", "pack.kill", "assist", "research.level", "store.buy", "arena.fight", "expedition.clear", "raid.win", "unit.level", "hive.damage", "defence", "workshop.pull", "siege.wave", "credits.spent", "boss.damage", "ruin", "raid.win", "build.level"];
  const source: ScoreSource = index === 0 || index === 9 ? "build.level" : index === 6 ? "store.buy" : index < 6 ? sources[(chapter + index) % sources.length]! : sources[(chapter * 3 + index) % sources.length]!;
  const per = source === "gather" ? 1000 : source === "hive.damage" || source === "boss.damage" ? 100 : source === "credits.spent" ? 10 : undefined;
  const target = source === "build.level" ? level + index : source === "gather" ? 10 * level : Math.max(1, Math.round((index + 1) * Math.max(1, level / 2)));
  const link = source === "build.level" ? buildingId : source === "store.buy" || source === "credits.spent" ? `store:${tab}` : theme.focus;
  const reward: Bundle = index === 9 ? { items: [{ itemId: "itm.chest_silver", qty: 1 }, { itemId: "itm.cdc", qty: 20 * level }], credits: 10 * level } : { items: [sp("general", index % 2 === 0 ? 15 : 60, 1 + Math.floor(index / 3))], credits: 2 * level };
  const names: Record<ScoreSource, [string, string]> = {
    "build.level": [`Gebäudestufen erreichen: ${target}`, `Reach ${target} building levels`],
    gather: [`${target}k Ressourcen sammeln`, `Gather ${target}k resources`],
    "workshop.pull": [`${target}× fabrizieren`, `Fabricate ${target}×`],
    "pack.kill": [`${target} Rudel vernichten`, `Kill ${target} packs`],
    assist: [`${target}× Nachbarn helfen`, `Assist neighbours ${target}×`],
    "research.level": [`${target} Forschungen`, `${target} researches`],
    "store.buy": [`${target} Angebote kaufen (Tab ${tab})`, `Buy ${target} offers (tab ${tab})`],
    "arena.fight": [`${target} Arenakämpfe`, `${target} arena fights`],
    "expedition.clear": [`${target} Expeditionsstufen`, `Clear ${target} expedition stages`],
    "raid.win": [`${target} Überfälle gewinnen`, `Win ${target} raids`],
    "unit.level": [`${target} Einheitenstufen`, `${target} unit levels`],
    "hive.damage": [`${target * 100} Hive-Schaden`, `${target * 100} hive damage`],
    defence: [`${target} Verteidigungen`, `${target} defences`],
    "siege.wave": [`${target} Belagerungswellen`, `${target} siege waves`],
    "credits.spent": [`${target * 10} ₵ ausgeben`, `Spend ${target * 10} ₵`],
    "boss.damage": [`${target * 100} Boss-Schaden`, `${target * 100} boss damage`],
    ruin: [`${target} Ruinen erkunden`, `Explore ${target} ruins`],
    "elite.kill": [`${target} Eliten`, `${target} elites`],
    "stamina.spent": [`${target} Ausdauer`, `${target} stamina`],
    chat: [`${target} Nachrichten`, `${target} messages`],
    "arena.win": [`${target} Arenasiege`, `${target} arena wins`],
    login: [`${target} Logins`, `${target} logins`],
    "mail.claim": [`${target} Post abholen`, `Claim ${target} mails`],
    repair: [`${target} Reparaturen`, `${target} repairs`],
    "mission.daily": [`${target} Tagesaufträge`, `${target} daily missions`],
    intel: [`${target} Intel-Missionen`, `${target} intel missions`],
    "gacha.open": [`${target} Truhen öffnen`, `Open ${target} chests`],
    "relic.hold": [`${target} Relikt-Stunden`, `${target} relic hours`],
    "hunt.kill": [`${target} Jagdziele erlegen`, `Kill ${target} hunt targets`],
  };
  const nm = names[source];
  return { id: `gc.${chapter}.${index + 1}`, chapter, name: N(nm[0], nm[1]), source, target, points: 10, link, ...(per ? { per } : {}), reward };
}

export const GROWTH_CHAIN: readonly GrowthMissionDef[] = CHAPTER_THEMES.flatMap((theme, c) => Array.from({ length: 10 }, (_, i) => growthMission(c + 1, i, theme)));
export const GROWTH_CHAPTERS: readonly { chapter: number; name: { de: string; en: string }; focus: string; reward: Bundle; unit?: string }[] = CHAPTER_THEMES.map((t, i) => {
  const chapter = i + 1;
  const unit = chapter === 5 ? "unit.striker_beta" : chapter === 10 ? "unit.medic_prime" : chapter === 15 ? "unit.aurora_node" : chapter === 20 ? "unit.aegis_prime" : undefined;
  return { chapter, name: N(t.title[0], t.title[1]), focus: t.focus, reward: { items: [{ itemId: chapter >= 15 ? "itm.chest_gold" : "itm.chest_silver", qty: 1 }, { itemId: "itm.fab", qty: Math.ceil(chapter / 5) }], credits: 20 * chapter }, ...(unit ? { unit } : {}) };
});

/** 120 achievements across six categories with points, titles and frames (R64). */
const ACHIEVEMENT_SPECS: { category: AchievementDef["category"]; source: ScoreSource; per?: number; steps: number[]; names: [string, string]; title?: string; frame?: string }[] = [
  { category: "building", source: "build.level", steps: [10, 30, 60, 100, 150, 220, 300, 400, 520, 650], names: ["Gebäudestufen", "Building levels"], title: "builder" },
  { category: "building", source: "research.level", steps: [5, 15, 30, 50, 80, 120, 170, 230, 300, 400], names: ["Forschungen", "Researches"], title: "architect" },
  { category: "units", source: "workshop.pull", steps: [10, 30, 60, 100, 200, 350, 500, 750, 1000, 1500], names: ["Fabrikationen", "Fabrications"], title: "collector" },
  { category: "units", source: "unit.level", steps: [20, 50, 100, 200, 350, 500, 750, 1000, 1500, 2000], names: ["Einheitenstufen", "Unit levels"], frame: "collector" },
  { category: "combat", source: "pack.kill", steps: [10, 50, 100, 250, 500, 1000, 2000, 3500, 5000, 10000], names: ["Rudel vernichtet", "Packs killed"], title: "hunter" },
  { category: "combat", source: "arena.win", steps: [5, 20, 50, 100, 200, 350, 500, 750, 1000, 1500], names: ["Arenasiege", "Arena wins"], title: "gladiator" },
  { category: "world", source: "gather", per: 1000, steps: [100, 500, 1000, 2500, 5000, 10000, 20000, 40000, 70000, 100000], names: ["k Ressourcen gesammelt", "k resources gathered"] },
  { category: "world", source: "raid.win", steps: [1, 5, 10, 25, 50, 100, 200, 350, 500, 1000], names: ["Überfälle gewonnen", "Raids won"], title: "raider" },
  { category: "store", source: "credits.spent", per: 100, steps: [1, 5, 10, 25, 50, 100, 250, 500, 1000, 5000], names: ["×100 ₵ ausgegeben", "×100 ₵ spent"], title: "patron" },
  { category: "store", source: "store.buy", steps: [1, 5, 10, 25, 50, 100, 200, 350, 500, 1000], names: ["Käufe", "Purchases"], title: "whale" },
  { category: "social", source: "chat", steps: [10, 50, 100, 250, 500, 1000, 2000, 3500, 5000, 10000], names: ["Nachrichten", "Messages"] },
  { category: "social", source: "assist", steps: [5, 20, 50, 100, 200, 350, 500, 750, 1000, 2000], names: ["Nachbarn geholfen", "Neighbours assisted"], frame: "gladiator" },
];

export const ACHIEVEMENTS: readonly AchievementDef[] = ACHIEVEMENT_SPECS.flatMap((spec, s) =>
  spec.steps.map((target, i) => ({
    id: `ach.${spec.source.replace(".", "_")}.${i + 1}`,
    name: N(`${spec.names[0]}: ${target}`, `${spec.names[1]}: ${target}`),
    category: spec.category,
    source: spec.source,
    target,
    ...(spec.per ? { per: spec.per } : {}),
    points: 10 * (i + 1),
    reward: i === 9 ? { items: [{ itemId: "itm.chest_gold", qty: 1 }], credits: 100 } : i >= 6 ? { items: [{ itemId: "itm.chest_silver", qty: 1 }], credits: 20 } : { items: [{ itemId: "itm.cdc", qty: 10 * (i + 1) }], credits: 5 },
    ...(i === 9 && spec.title ? { title: spec.title } : {}),
    ...(i === 9 && spec.frame ? { frame: spec.frame } : {}),
    ...(s === 0 && i === 0 ? {} : {}),
  })),
);

/**
 * Battle pass: 50 tiers (+10 premium+), points from daily (5) and weekly (7) pass missions (R65). Since D115 the
 * track varies: the free lane rotates speedups, crates and training data, every fifth tier fragments and data cores,
 * every tenth a silver chest; the premium lane rotates XP, nano, long speedups and cores, every fifth tier
 * fabrication tokens, and every tenth is a highlight — spotlight tokens, gold chests, S fragments — up to tier 50.
 */
const PASS_HIGHLIGHTS: Record<number, Bundle> = {
  10: { items: [{ itemId: "itm.ffab", qty: 1 }, { itemId: "itm.chest_gold", qty: 1 }] },
  20: { items: [{ itemId: "itm.ufr_s", qty: 20 }, { itemId: "itm.ffab", qty: 1 }] },
  30: { items: [{ itemId: "itm.ffab", qty: 2 }, { itemId: "itm.chest_gold", qty: 1 }, { itemId: "itm.proto_blueprint", qty: 1 }], credits: 100 },
  40: { items: [{ itemId: "itm.ufr_s", qty: 30 }, { itemId: "itm.ffab", qty: 2 }] },
  50: { items: [{ itemId: "itm.ffab", qty: 3 }, { itemId: "itm.chest_gold", qty: 2 }, { itemId: "itm.merit_order", qty: 1 }], credits: 200 },
  60: { items: [{ itemId: "itm.ffab", qty: 5 }, { itemId: "itm.ufr_s", qty: 50 }] },
};
export const BATTLE_PASS_TIERS: readonly PassTierDef[] = Array.from({ length: 60 }, (_, i) => {
  const tier = i + 1;
  const freeCycle: Bundle[] = [{ items: [sp("general", 60, 1)] }, { items: [crate("rat", "10k", 2)] }, { items: [{ itemId: "itm.xp_2000", qty: 1 }] }, { items: [crate("pol", "10k", 2)] }];
  const premiumCycle: Bundle[] = [{ items: [{ itemId: "itm.xp_10000", qty: 1 }], credits: 10 }, { items: [{ itemId: "itm.nano", qty: 20 }] }, { items: [sp("general", 180, 1)] }, { items: [{ itemId: "itm.cdc", qty: 20 }, { itemId: "itm.ufr_a", qty: 3 }] }];
  const free: Bundle = tier % 10 === 0 ? { items: [{ itemId: "itm.chest_silver", qty: 1 }, { itemId: "itm.fab", qty: 1 }, { itemId: "itm.research_badge", qty: 3 }] } : tier % 5 === 0 ? { items: [{ itemId: "itm.ufr_b", qty: 10 }, { itemId: "itm.cdc", qty: 20 }] } : freeCycle[tier % 4]!;
  const premium: Bundle = PASS_HIGHLIGHTS[tier] ?? (tier % 5 === 0 ? { items: [{ itemId: "itm.fab", qty: 2 }, { itemId: "itm.ufr_a", qty: 10 }] } : premiumCycle[tier % 4]!);
  return { tier, points: tier * 100, free, premium };
});
export const BATTLE_PASS_DAYS = 28;
export const BATTLE_PASS_BASE_TIERS = 50;

/** The weekly pass (D116): 20 tiers of 60 points over 7 days, fed by the same pass missions as the battle pass. */
const WEEK_HIGHLIGHTS: Record<number, Bundle> = {
  10: { items: [{ itemId: "itm.ffab", qty: 1 }, { itemId: "itm.ufr_a", qty: 10 }] },
  20: { items: [{ itemId: "itm.ffab", qty: 1 }, { itemId: "itm.chest_gold", qty: 1 }, { itemId: "itm.ufr_s", qty: 10 }], credits: 100 },
};
export const WEEK_PASS_TIERS: readonly PassTierDef[] = Array.from({ length: 20 }, (_, i) => {
  const tier = i + 1;
  const freeCycle: Bundle[] = [{ items: [crate("rat", "10k", 2)] }, { items: [sp("general", 60, 1)] }, { items: [crate("pol", "10k", 2)] }, { items: [{ itemId: "itm.xp_2000", qty: 1 }] }];
  const premiumCycle: Bundle[] = [{ items: [{ itemId: "itm.nano", qty: 15 }] }, { items: [{ itemId: "itm.cdc", qty: 15 }] }, { items: [sp("general", 180, 1)] }, { items: [{ itemId: "itm.xp_10000", qty: 1 }] }];
  const free: Bundle = tier % 5 === 0 ? { items: [{ itemId: "itm.fab", qty: 1 }, { itemId: "itm.ufr_b", qty: 5 }, { itemId: "itm.research_badge", qty: 1 }] } : freeCycle[tier % 4]!;
  const premium: Bundle = WEEK_HIGHLIGHTS[tier] ?? (tier % 5 === 0 ? { items: [{ itemId: "itm.fab", qty: 2 }, { itemId: "itm.ufr_a", qty: 5 }] } : premiumCycle[tier % 4]!);
  return { tier, points: tier * 60, free, premium };
});

/** Every pass of kind battle has a tier track: its tiers, the tiers without premium+, the credits for one tier. */
export interface TierTrackDef { tiers: readonly PassTierDef[]; baseTiers: number; tierCredits: number }
export const TIER_TRACKS: Readonly<Record<string, TierTrackDef>> = {
  "pass.battle": { tiers: BATTLE_PASS_TIERS, baseTiers: BATTLE_PASS_BASE_TIERS, tierCredits: BATTLE_PASS_TIER_CREDITS },
  "pass.week": { tiers: WEEK_PASS_TIERS, baseTiers: 20, tierCredits: 100 },
};
export function tierTrack(passId: string): TierTrackDef {
  const track = TIER_TRACKS[passId];
  if (!track) throw new Error(`no tier track for ${passId}`);
  return track;
}

export const PASS_MISSIONS_DAILY: readonly MissionDef[] = [
  { id: "pm.d.build", name: N("Ein Gebäude ausbauen", "Upgrade a building"), source: "build.level", target: 1, points: 20 },
  { id: "pm.d.kill", name: N("Zwei Rudel vernichten", "Kill two packs"), source: "pack.kill", target: 2, points: 20 },
  { id: "pm.d.gather", name: N("10 000 Ressourcen sammeln", "Gather 10 000 resources"), source: "gather", target: 10, per: 1000, points: 20 },
  { id: "pm.d.stamina", name: N("20 Ausdauer ausgeben", "Spend 20 stamina"), source: "stamina.spent", target: 20, points: 20 },
  { id: "pm.d.login", name: N("Einloggen", "Log in"), source: "login", target: 1, points: 20 },
];
export const PASS_MISSIONS_WEEKLY: readonly MissionDef[] = [
  { id: "pm.w.build", name: N("Zehn Gebäudestufen", "Ten building levels"), source: "build.level", target: 10, points: 100 },
  { id: "pm.w.research", name: N("Drei Forschungen", "Three researches"), source: "research.level", target: 3, points: 100 },
  { id: "pm.w.pull", name: N("Zehnmal fabrizieren", "Fabricate ten times"), source: "workshop.pull", target: 10, points: 100 },
  { id: "pm.w.kill", name: N("Zwanzig Rudel", "Twenty packs"), source: "pack.kill", target: 20, points: 100 },
  { id: "pm.w.arena", name: N("Zehn Arenakämpfe", "Ten arena fights"), source: "arena.fight", target: 10, points: 100 },
  { id: "pm.w.assist", name: N("Fünf Hilfen", "Five assists"), source: "assist", target: 5, points: 100 },
  { id: "pm.w.spend", name: N("500 ₵ ausgeben", "Spend 500 ₵"), source: "credits.spent", target: 50, per: 10, points: 100 },
];

export function missionById(id: string): MissionDef | GrowthMissionDef | undefined {
  return [...DAILY_MISSIONS, ...PASS_MISSIONS_DAILY, ...PASS_MISSIONS_WEEKLY, ...GROWTH_CHAIN].find((m) => m.id === id);
}
